import {Icon} from '@/app/components/Icon';
import {useMemo, useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {ContentHints} from '@/components/app/hint';
import {cn} from '@/lib/utils';
import {useGetPhraseFileQuery} from '../../../app/services/fileApi';
import {getOrderedBiobookPageFileNames} from '../helpers/utils';
import {BiobookPage} from '../types';
import {AbbreviationLongName} from './AbbreviationLongName';
import {BiobookPageImage} from './BiobookPageImage';
import styles from './BiobookPageGallery.module.css';
export interface BiobookPageGalleryProps {
  biobookPage: BiobookPage;
  className?: string;
}
export const BiobookPageGallery = ({biobookPage, className}: BiobookPageGalleryProps) => {
  const fileNames = useMemo(() => getOrderedBiobookPageFileNames(biobookPage), [biobookPage.fileNames]);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const activeFileName = fileNames[activeFileIndex];
  const [activeAbbreviationShortName, setActiveAbbreviationShortName] = useState('');
  const {
    data: file,
    error: errorFile,
    isFetching: isFetchingFile,
  } = useGetPhraseFileQuery({
    fileName: activeFileName,
    phraseId: biobookPage.phraseId,
    thumb: false,
  });
  const tooltipContainer = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(true);
  return (
    <div className={cn('flex', className)} ref={tooltipContainer}>
      <div
        className="shrink-0 overflow-auto w-120 bg-zinc-100 p-6"
        style={{
          maxWidth: '25%',
        }}>
        <div
          dangerouslySetInnerHTML={{
            __html: biobookPage.phraseBodyHtml,
          }}
        />
      </div>
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {isFetchingFile && (
          <div className="flex-1 flex items-center justify-center">
            <Icon icon="spinner" spin />
          </div>
        )}
        {!isFetchingFile && errorFile && (
          <div className="flex-1 flex items-center justify-center">
            <Icon icon="ban" />
          </div>
        )}
        {!isFetchingFile && !errorFile && file && (
          <div
            className={cn('flex-1 overflow-auto p-6', {
              'min-h-0': !fit,
              'h-full': fit,
            })}
            onClick={() => setFit(!fit)}
            role={'button'}
            style={{
              cursor: fit ? 'zoom-in' : 'zoom-out',
            }}>
            <img
              alt=""
              className={cn('block max-w-full mx-auto', {
                'max-h-full': fit,
              })}
              src={file}
            />
          </div>
        )}
        {fileNames.length > 1 && (
          <div className={'flex items-center justify-center gap-6 p-6 mx-6 border-t border-zinc-400'}>
            <Button
              disabled={activeFileIndex === 0}
              onClick={() => setActiveFileIndex(activeFileIndex - 1)}
              variant="text"
              className="rounded-full"
              type="button"
              startIcon={<Icon icon={'arrow-left'} />}
              aria-label={'Back'}
              size="icon"
            />
            <div className={'overflow-auto flex-1 min-w-0'}>
              <div className={'flex items-center justify-center gap-6 p-2 w-fit'}>
                {fileNames.map((fileName, index) => (
                  <BiobookPageImage
                    biobookPage={biobookPage}
                    className={cn(styles.biobookPage, {
                      [styles.selected]: activeFileIndex === index,
                    })}
                    figCaption={`Fig. ${index + 1}`}
                    fileName={fileName}
                    key={index}
                    onClick={() => setActiveFileIndex(index)}
                  />
                ))}
              </div>
            </div>
            <Button
              disabled={activeFileIndex === fileNames.length - 1}
              onClick={() => setActiveFileIndex(activeFileIndex + 1)}
              variant="text"
              className="rounded-full"
              type="button"
              startIcon={<Icon icon={'arrow-right'} />}
              aria-label={'arrow right'}
              size="icon"
            />
          </div>
        )}
      </div>
      <ContentHints
        shouldHide={() => setActiveAbbreviationShortName('')}
        shouldShow={(e) => setActiveAbbreviationShortName(e.target.innerText)}
        style={{
          marginTop: '-1rem',
        }}
        target={`[data-slot="dialog-content"] .b-abbr`}>
        {activeAbbreviationShortName && <AbbreviationLongName shortName={activeAbbreviationShortName} />}
      </ContentHints>
    </div>
  );
};
